import type {
  Benefit,
  FailureItem,
  FaqItem,
  MechanismStep,
  Module,
  OutcomeCard,
  ProofItem,
} from '../types/content'

/** Ancla interna: los CTA secundarios llevan al bloque de precio. */
export const CTA_URL = '#comprar'

/** Checkout real de Hotmart (solo el botón de compra del bloque de precio). */
export const CHECKOUT_URL = 'https://pay.hotmart.com/T107633655N?checkoutMode=10'

export const guaranteeStats: { value: number; label: string; window: string }[] = [
  { value: 94, label: 'Ven energía estable', window: 'en 7 días' },
  { value: 89, label: 'Los antojos desaparecen', window: 'en 14 días' },
  { value: 76, label: 'Cambios físicos visibles', window: 'en 21 días' },
]

export const mechanismSteps: MechanismStep[] = [
  { icon: 'spike', text: 'Un pico de glucosa dispara insulina.' },
  { icon: 'lock', text: 'La insulina bloquea tu quema de grasa.' },
  { icon: 'clock', text: '2 horas después te deja con hambre de tiburón.' },
  { icon: 'loop', text: 'El ciclo se repite. Otra vez. Y otra.' },
]

export const outcomeCards: OutcomeCard[] = [
  {
    icon: 'sun',
    when: 'Viernes, 4 PM',
    text: 'Energía clara. Mente enfocada. Cero antojo de algo dulce.',
  },
  {
    icon: 'moon',
    when: 'Noche',
    text: 'Cenas comida real, sin culpa. Y duermes profundo de corrido.',
  },
  {
    icon: 'sunrise',
    when: 'Mañana',
    text: 'Despiertas con energía. Y la grasa abdominal finalmente baja.',
  },
]

export const failures: FailureItem[] = [
  {
    method: 'Contar calorías',
    explanation:
      'consume tu energía mental todo el día y no dice nada sobre lo que tu glucosa está haciendo después de comer.',
  },
  {
    method: 'Dietas bajas en carbohidratos',
    explanation:
      'son tan restrictivas que las abandonas en tres semanas, y el rebote te deja peor que al empezar.',
  },
  {
    method: 'Comer limpio',
    explanation:
      'sin orden, sigues generando picos: una fruta sola en ayunas te dispara igual que un postre.',
  },
  {
    method: 'Apps de nutrición',
    explanation:
      'te dan números genéricos, no personalizan el orden ni el contexto real de tu día.',
  },
  {
    method: 'Suplementos',
    explanation:
      'son un parche sobre el problema. Nada compensa una secuencia de comida que te deja en montaña rusa.',
  },
]

export const modules: Module[] = [
  {
    icon: 'science',
    label: 'Módulo 1',
    title: 'La Ciencia Detrás De Los Picos',
    description:
      'Qué pasa exactamente en tu cuerpo entre el primer bocado y el antojo de las 4 PM, explicado sin jerga médica.',
  },
  {
    icon: 'code',
    label: 'Módulo 2',
    title: 'El Código De Secuenciación',
    description:
      'El orden exacto en que debes comer lo que ya tienes en tu plato. La regla completa en una página.',
  },
  {
    icon: 'map',
    label: 'Módulo 3',
    title: 'Protocolos Por Escenario',
    description:
      'Restaurante, reunión de trabajo, cumpleaños, viaje, día sin tiempo. Un guion para cada situación real.',
  },
  {
    icon: 'eye',
    label: 'Módulo 4',
    title: 'La Guía Visual De Alimentos',
    description:
      'Tablas visuales para armar cualquier comida en segundos, sin pesar, sin medir, sin calcular.',
  },
  {
    icon: 'brake',
    label: 'Bonus',
    title: 'Checklist De Freno De Emergencia',
    description:
      'Los 4 movimientos para cortar un antojo en menos de 10 minutos cuando ya empezó.',
  },
  {
    icon: 'cycle',
    label: 'Bonus',
    title: 'Protocolo Para Ciclo Menstrual',
    description:
      'Cómo ajustar la secuencia en cada fase para que la fase lútea no arruine tu semana.',
  },
]

export const benefits: Benefit[] = [
  {
    headline: 'Energía estable a las 3 PM',
    detail: 'sin café de rescate ni siesta secreta en la oficina.',
  },
  {
    headline: 'Adiós atracones nocturnos',
    detail: 'la cocina deja de llamarte a las 10 de la noche.',
  },
  {
    headline: 'Grasa abdominal desbloqueada',
    detail: 'cuando la insulina baja, tu cuerpo vuelve a poder usar su reserva.',
  },
  {
    headline: 'Libertad de contar calorías',
    detail: 'cambias la aritmética diaria por una sola regla de orden.',
  },
  {
    headline: 'Claridad mental',
    detail: 'menos niebla, más foco sostenido en lo que sí importa.',
  },
  {
    headline: 'Sueño profundo',
    detail: 'sin despertares a las 3 AM por una glucosa en caída.',
  },
  {
    headline: 'Libertad social',
    detail: 'comes en restaurantes y cumpleaños sin explicaciones ni culpa.',
  },
]

export const proofItems: ProofItem[] = [
  {
    icon: 'science',
    title: 'Estudio Stanford, 2023',
    description:
      'hasta 32% menos picos de glucosa comiendo exactamente los mismos alimentos, solo cambiando el orden.',
    counter: 32,
    counterSuffix: '%',
  },
  {
    icon: 'dna',
    title: 'Biología básica',
    description:
      'fibra y proteína primero frenan la absorción del azúcar. El orden cambia la respuesta hormonal, no solo las calorías.',
  },
  {
    icon: 'stethoscope',
    title: 'Investigación en nutrición',
    description:
      'hasta 89% menos reportes de antojos intensos cuando la comida empieza por vegetales y proteína.',
    counter: 89,
    counterSuffix: '%',
  },
  {
    icon: 'chart',
    title: 'Datos reales',
    description:
      'mujeres aplicando secuenciación reportan energía sostenida en la tarde dentro de la primera semana.',
    counter: 5000,
    counterSuffix: '+',
  },
]

export const faqs: FaqItem[] = [
  {
    question: '¿Es otro plan de dieta restrictiva?',
    answer:
      'No. Comes los mismos alimentos que ya comes. Lo único que cambia es el orden en que los pones en tu boca y el contexto de cada comida. No hay lista de prohibidos.',
  },
  {
    question: '¿Cuánto tiempo toma ver resultados?',
    answer:
      'Muchas mujeres reportan una tarde distinta el mismo día que aplican la secuencia. Energía sostenida y menos antojos suelen notarse en los primeros 3 a 7 días; los cambios de composición corporal toman algunas semanas.',
  },
  {
    question: '¿Funciona si como fuera de casa casi todos los días?',
    answer:
      'Totalmente. El protocolo está diseñado para la vida real: hay un guion específico para restaurantes, comida corrida, reuniones de trabajo y viajes. No necesitas cocinar para aplicarlo.',
  },
  {
    question: '¿Y si tengo restricciones dietéticas o alergias?',
    answer:
      'El protocolo se adapta. Es una regla de orden, no un menú: funciona igual siendo vegetariana, sin gluten, sin lácteos o con cualquier preferencia cultural de LATAM.',
  },
  {
    question: '¿Necesito suplementos o ejercicio para que funcione?',
    answer:
      'No. Este protocolo funciona solo con la comida que ya compras. Si además te mueves, los resultados llegan más rápido, pero no es un requisito para empezar.',
  },
]
